/**
 * MusicXML Merger Utility
 *
 * Merges separate notation and TAB parts in MusicXML files into a single two-staff part.
 * This enables proper display of guitar music with both standard notation and tablature.
 */

export interface MergeResult {
  xmlContent: string;
  wasMerged: boolean;
  wasModified: boolean;  // True if content was changed (merge OR instrument fix)
  message: string;
}

interface PartInfo {
  id: string;
  element: Element;
  clefSign: string | null;
  isNotation: boolean;
  isTab: boolean;
  hasPitchData: boolean;  // TAB parts with pitch data shouldn't be merged
}

/**
 * Extract the clef sign from a part element
 * @param partElement - The part Element from the MusicXML document
 * @returns The clef sign (e.g., 'G', 'F', 'C', 'TAB') or null if not found
 */
function getClefSign(partElement: Element): string | null {
  const clefElement = partElement.querySelector('attributes clef sign');
  return clefElement ? clefElement.textContent?.trim() || null : null;
}

/**
 * Check if a clef sign represents standard musical notation
 * @param clefSign - The clef sign to check
 * @returns True if the clef is G, F, or C (standard notation clefs)
 */
function isNotationClef(clefSign: string | null): boolean {
  if (!clefSign) return false;
  return ['G', 'F', 'C'].includes(clefSign.toUpperCase());
}

/**
 * Check if a clef sign represents tablature
 * @param clefSign - The clef sign to check
 * @returns True if the clef is TAB
 */
function isTabClef(clefSign: string | null): boolean {
  if (!clefSign) return false;
  return clefSign.toUpperCase() === 'TAB';
}

/**
 * Analyze all parts in the MusicXML document
 * @param doc - The parsed MusicXML document
 * @returns Array of PartInfo objects describing each part
 */
function analyzeParts(doc: Document): PartInfo[] {
  const parts = Array.from(doc.querySelectorAll('part'));

  return parts.map(partElement => {
    const id = partElement.getAttribute('id') || '';
    const clefSign = getClefSign(partElement);
    // Check if this part has pitch data in its notes
    const hasPitchData = partElement.querySelector('note pitch') !== null;

    return {
      id,
      element: partElement,
      clefSign,
      isNotation: isNotationClef(clefSign),
      isTab: isTabClef(clefSign),
      hasPitchData
    };
  });
}

/**
 * Check if a part has multi-voice content (uses backup elements for voice layering)
 */
function hasMultiVoiceContent(partElement: Element): boolean {
  // Check for backup elements which indicate multi-voice content
  const backups = partElement.querySelectorAll('measure backup');
  if (backups.length === 0) return false;

  // Check for multiple voice numbers
  const voices = new Set<string>();
  const voiceElements = partElement.querySelectorAll('note voice');
  voiceElements.forEach(v => {
    if (v.textContent) voices.add(v.textContent.trim());
  });



  return voices.size > 1;
}

/**
 * Determine if the parts can be merged
 * @param parts - Array of PartInfo objects
 * @returns True if exactly 2 parts exist with one notation and one TAB part
 */
function canMerge(parts: PartInfo[]): boolean {
  if (parts.length !== 2) {
    return false;
  }

  const notationParts = parts.filter(p => p.isNotation);
  const tabParts = parts.filter(p => p.isTab);

  return notationParts.length === 1 && tabParts.length === 1;
}

/**
 * Check if any part has multi-voice content that should be preserved
 */
function shouldPreserveVoices(parts: PartInfo[]): boolean {
  for (const part of parts) {
    if (hasMultiVoiceContent(part.element)) {
      return true;
    }
  }
  return false;
}

/**
 * Get the measure duration based on time signature and divisions
 * This is more reliable than summing note durations for multi-voice content
 */
function getMeasureDuration(measure: Element, doc: Document): number {
  // First, try to get from this measure's attributes
  let attributes = measure.querySelector('attributes');

  // If not in this measure, search backwards in previous measures of the same part
  if (!attributes) {
    const part = measure.parentElement;
    if (part) {
      const measures = Array.from(part.querySelectorAll('measure'));
      const currentIndex = measures.indexOf(measure);
      for (let i = currentIndex - 1; i >= 0; i--) {
        attributes = measures[i].querySelector('attributes');
        if (attributes && attributes.querySelector('divisions')) break;
      }
    }
  }

  if (!attributes) {
    // Fallback: try first measure of document
    attributes = doc.querySelector('part measure attributes');
  }

  // Get divisions (ticks per quarter note)
  const divisionsEl = attributes?.querySelector('divisions');
  const divisions = divisionsEl ? parseInt(divisionsEl.textContent || '256', 10) : 256;

  // Get time signature
  const beatsEl = attributes?.querySelector('time beats');
  const beatTypeEl = attributes?.querySelector('time beat-type');
  const beats = beatsEl ? parseInt(beatsEl.textContent || '4', 10) : 4;
  const beatType = beatTypeEl ? parseInt(beatTypeEl.textContent || '4', 10) : 4;

  // Calculate measure duration: (beats / beat-type) * 4 * divisions
  // For 4/4 with divisions=256: (4/4) * 4 * 256 = 1024
  // For 3/4 with divisions=256: (3/4) * 4 * 256 = 768
  return Math.round((beats / beatType) * 4 * divisions);
}

/**
 * Perform the actual merge of notation and TAB parts into a single two-staff part
 * @param doc - The parsed MusicXML document
 * @param notationPart - The notation part info
 * @param tabPart - The TAB part info
 */
function performMerge(doc: Document, notationPart: PartInfo, tabPart: PartInfo): void {
  // Step 1: Remove TAB part from <part-list>
  const partList = doc.querySelector('part-list');
  if (partList) {
    const tabScorePart = partList.querySelector(`score-part[id="${tabPart.id}"]`);
    if (tabScorePart) {
      tabScorePart.remove();
    }
  }

  // Step 2: Get all measures from both parts
  const notationMeasures = Array.from(notationPart.element.querySelectorAll('measure'));
  const tabMeasures = Array.from(tabPart.element.querySelectorAll('measure'));

  // Step 3: Update first measure attributes
  if (notationMeasures.length > 0) {
    const firstMeasure = notationMeasures[0];
    let attributes = firstMeasure.querySelector('attributes');

    // Create attributes element if it doesn't exist
    if (!attributes) {
      attributes = doc.createElement('attributes');
      const firstChild = firstMeasure.firstChild;
      if (firstChild) {
        firstMeasure.insertBefore(attributes, firstChild);
      } else {
        firstMeasure.appendChild(attributes);
      }
    }

    // Add <staves>2</staves>
    let stavesElement = attributes.querySelector('staves');
    if (!stavesElement) {
      stavesElement = doc.createElement('staves');
      stavesElement.textContent = '2';
      // Insert staves as first child of attributes
      const firstAttrChild = attributes.firstChild;
      if (firstAttrChild) {
        attributes.insertBefore(stavesElement, firstAttrChild);
      } else {
        attributes.appendChild(stavesElement);
      }
    } else {
      stavesElement.textContent = '2';
    }

    // Ensure notation clef has number="1"
    const notationClef = attributes.querySelector('clef');
    if (notationClef && !notationClef.getAttribute('number')) {
      notationClef.setAttribute('number', '1');
    }

    // Remove octave-change from transpose element to prevent AlphaTab from
    // transposing the notation display. Guitar notation should show written pitch.
    const transpose = attributes.querySelector('transpose');
    if (transpose) {
      const octaveChange = transpose.querySelector('octave-change');
      if (octaveChange) {
        octaveChange.remove();


      }
    }

    // Copy TAB clef with number="2"
    if (tabMeasures.length > 0) {
      const tabFirstMeasure = tabMeasures[0];
      const tabAttributes = tabFirstMeasure.querySelector('attributes');

      if (tabAttributes) {
        const tabClef = tabAttributes.querySelector('clef');
        if (tabClef) {
          const clefClone = tabClef.cloneNode(true) as Element;
          clefClone.setAttribute('number', '2');
          attributes.appendChild(clefClone);
        }

        // Copy staff-details (tuning info) with number="2"
        const staffDetails = tabAttributes.querySelector('staff-details');
        if (staffDetails) {
          const staffDetailsClone = staffDetails.cloneNode(true) as Element;
          staffDetailsClone.setAttribute('number', '2');
          attributes.appendChild(staffDetailsClone);
        }
      }
    }
  }

  // Step 4: Process all measures - add staff elements and merge TAB content
  notationMeasures.forEach((notationMeasure, index) => {
    // Ensure all notation notes have <staff>1</staff>
    const notationNotes = Array.from(notationMeasure.querySelectorAll('note'));
    notationNotes.forEach(note => {
      // Add staff element
      let staffElement = note.querySelector('staff');
      if (!staffElement) {
        staffElement = doc.createElement('staff');
        staffElement.textContent = '1';
        // Insert staff element after pitch/rest but before other elements
        const duration = note.querySelector('duration');
        if (duration && duration.nextSibling) {
          note.insertBefore(staffElement, duration.nextSibling);
        } else {
          note.appendChild(staffElement);
        }
      } else {
        staffElement.textContent = '1';
      }
      // Note: MusicXML pitches are already correct - no octave transposition needed
      // Guitar is a transposing instrument but MusicXML stores sounding pitch
    });

    // Match with corresponding TAB measure (by number attribute or index)
    let tabMeasure: Element | null = null;
    const measureNumber = notationMeasure.getAttribute('number');

    if (measureNumber) {
      tabMeasure = tabMeasures.find(m => m.getAttribute('number') === measureNumber) || null;
    }

    if (!tabMeasure && index < tabMeasures.length) {
      tabMeasure = tabMeasures[index];
    }

    if (tabMeasure) {
      // Get measure duration from time signature (reliable for multi-voice content)
      const measureDuration = getMeasureDuration(notationMeasure, doc);

      // Add backup element to return to measure start before TAB content
      if (measureDuration > 0) {
        const backupElement = doc.createElement('backup');
        const backupDuration = doc.createElement('duration');
        backupDuration.textContent = measureDuration.toString();
        backupElement.appendChild(backupDuration);
        notationMeasure.appendChild(backupElement);
      }

      // Copy all children from TAB measure IN ORDER to preserve timing structure
      // (notes, backups, forwards must remain in sequence for multi-voice content)
      const tabChildren = Array.from(tabMeasure.childNodes);
      tabChildren.forEach(child => {
        if (child.nodeType !== Node.ELEMENT_NODE) return;
        const element = child as Element;
        const tagName = element.tagName.toLowerCase();

        if (tagName === 'note') {
          const noteClone = element.cloneNode(true) as Element;

          // Convert <pitch> to <unpitched> to prevent TAB notes from displaying
          // on the notation staff. TAB notes use fret/string for display anyway.
          const pitchElement = noteClone.querySelector('pitch');
          if (pitchElement) {
            const step = pitchElement.querySelector('step')?.textContent || 'E';
            const octave = pitchElement.querySelector('octave')?.textContent || '4';

            const unpitched = doc.createElement('unpitched');
            const displayStep = doc.createElement('display-step');
            displayStep.textContent = step;
            const displayOctave = doc.createElement('display-octave');
            displayOctave.textContent = octave;
            unpitched.appendChild(displayStep);
            unpitched.appendChild(displayOctave);

            pitchElement.parentNode?.replaceChild(unpitched, pitchElement);
          }

          // Set staff to 2
          let staffElement = noteClone.querySelector('staff');
          if (!staffElement) {
            staffElement = doc.createElement('staff');
            staffElement.textContent = '2';
            const duration = noteClone.querySelector('duration');
            if (duration && duration.nextSibling) {
              noteClone.insertBefore(staffElement, duration.nextSibling);
            } else {
              noteClone.appendChild(staffElement);
            }
          } else {
            staffElement.textContent = '2';
          }

          notationMeasure.appendChild(noteClone);
        } else if (tagName === 'backup' || tagName === 'forward') {
          // Copy backup/forward IN ORDER to preserve timing
          const clone = element.cloneNode(true) as Element;
          if (tagName === 'forward') {
            let staffEl = clone.querySelector('staff');
            if (!staffEl) {
              staffEl = doc.createElement('staff');
              clone.appendChild(staffEl);
            }
            staffEl.textContent = '2';
          }
          notationMeasure.appendChild(clone);
        } else if (tagName === 'direction') {
          const directionClone = element.cloneNode(true) as Element;
          let staffEl = directionClone.querySelector('staff');
          if (!staffEl) {
            staffEl = doc.createElement('staff');
            directionClone.appendChild(staffEl);
          }
          staffEl.textContent = '2';
          notationMeasure.appendChild(directionClone);
        } else if (tagName === 'barline') {
          const location = element.getAttribute('location');
          const existingBarline = notationMeasure.querySelector(`barline[location="${location}"]`);
          if (!existingBarline) {
            notationMeasure.appendChild(element.cloneNode(true));
          }
        }
        // Skip: attributes, print, harmony (already in notation part)
      });
    }
  });

  // Step 5: Remove the TAB <part> element entirely
  tabPart.element.remove();
}

/**
 * Merge all voices into voice 1 to prevent AlphaTab from greying out secondary voices.
 * AlphaTab displays notes from non-primary voices with reduced opacity.
 * @param doc - The parsed MusicXML document
 * @returns true if any fixes were made
 */
function mergeVoicesToVoice1(doc: Document): boolean {
  const voiceElements = Array.from(doc.querySelectorAll('note voice'));
  let fixCount = 0;

  voiceElements.forEach(voiceEl => {
    const currentVoice = voiceEl.textContent?.trim();
    if (currentVoice && currentVoice !== '1') {
      voiceEl.textContent = '1';
      fixCount++;
    }
  });

  if (fixCount > 0) {


  }

  return fixCount > 0;
}

/**
 * Fix instrument ID references that don't match defined instruments
 * This handles cases where notes reference instruments from a different part (e.g., P2-I1)
 * but only one part is defined (e.g., P1).
 * @param doc - The parsed MusicXML document
 * @returns true if any fixes were made
 */
function fixInstrumentIds(doc: Document): boolean {
  // Get all defined score-parts and their instruments
  const scoreParts = Array.from(doc.querySelectorAll('score-part'));
  const definedInstruments = new Set<string>();
  const partInstrumentMap = new Map<string, string>(); // partId -> first instrumentId

  scoreParts.forEach(scorePart => {
    const partId = scorePart.getAttribute('id');
    const instruments = Array.from(scorePart.querySelectorAll('score-instrument'));
    instruments.forEach(inst => {
      const instId = inst.getAttribute('id');
      if (instId) {
        definedInstruments.add(instId);
        if (partId && !partInstrumentMap.has(partId)) {
          partInstrumentMap.set(partId, instId);
        }
      }
    });
  });

  if (definedInstruments.size === 0) {
    return false;
  }

  // Get all instrument references in notes
  const instrumentRefs = Array.from(doc.querySelectorAll('note instrument'));
  let fixCount = 0;

  // Get the first defined instrument as the fallback
  const fallbackInstrument = definedInstruments.values().next().value as string;
  if (!fallbackInstrument) {
    return false;
  }

  instrumentRefs.forEach(instRef => {
    const refId = instRef.getAttribute('id');
    if (refId && !definedInstruments.has(refId)) {
      // This instrument ID is not defined - fix it
      instRef.setAttribute('id', fallbackInstrument);
      fixCount++;
    }
  });

  if (fixCount > 0) {


  }

  return fixCount > 0;
}

/**
 * Merge separate notation and TAB parts in a MusicXML file into a single two-staff part
 * @param xmlContent - The MusicXML content as a string
 * @returns MergeResult containing the processed XML and merge status
 */
export function mergeMusicXmlParts(xmlContent: string): MergeResult {
  // Parse XML content using application/xml for stricter parsing
  const parser = new DOMParser();

  // Remove DOCTYPE declaration which can cause issues in some browsers
  const xmlWithoutDoctype = xmlContent.replace(/<!DOCTYPE[^>]*>/i, '');

  const doc = parser.parseFromString(xmlWithoutDoctype, 'application/xml');

  // Check for parse errors
  const parseError = doc.querySelector('parsererror');
  if (parseError) {
    console.error('MusicXML parse error:', parseError.textContent);
    return {
      xmlContent,
      wasMerged: false,
      wasModified: false,
      message: 'Failed to parse MusicXML: Invalid XML format'
    };
  }

  // Verify we have a valid document
  if (!doc.documentElement || doc.documentElement.nodeName === 'parsererror') {
    return {
      xmlContent,
      wasMerged: false,
      wasModified: false,
      message: 'Failed to parse MusicXML: Could not find root element'
    };
  }

  // Fix any instrument ID mismatches (common issue with Sibelius exports)
  const instrumentsFixed = fixInstrumentIds(doc);

  // Analyze parts FIRST (before any voice modifications)
  const parts = analyzeParts(doc);

  // Check if merge is possible (structural check: do we have notation + TAB?)
  const canMergeParts = canMerge(parts);

  // Check if we should preserve voice structure (multi-voice content like melody + bass)
  const preserveVoices = shouldPreserveVoices(parts);

  // Only merge voices to voice 1 if we're merging AND there's no multi-voice content
  // For multi-voice content (melody + bass), we need to preserve voice numbers
  let voicesMerged = false;
  if (canMergeParts && !preserveVoices) {
    voicesMerged = mergeVoicesToVoice1(doc);
  }

  if (!canMergeParts) {
    // Even if no merge needed, return fixed XML if any fixes were applied
    if (instrumentsFixed || voicesMerged) {
      const serializer = new XMLSerializer();
      let fixedXml = serializer.serializeToString(doc);

      // Add XML declaration if it was present in the original
      if (xmlContent.startsWith('<?xml')) {
        const xmlDeclMatch = xmlContent.match(/<\?xml[^?]*\?>/);
        if (xmlDeclMatch && !fixedXml.startsWith('<?xml')) {
          fixedXml = xmlDeclMatch[0] + '\n' + fixedXml;
        }
      }

      const fixes = [];
      if (instrumentsFixed) fixes.push('instrument IDs');
      if (voicesMerged) fixes.push('voice numbers');

      return {
        xmlContent: fixedXml,
        wasMerged: false,
        wasModified: true,
        message: `Fixed ${fixes.join(' and ')} (no merge needed)`
      };
    }

    if (parts.length === 0) {
      return {
        xmlContent,
        wasMerged: false,
        wasModified: false,
        message: 'No parts found in MusicXML file'
      };
    }

    if (parts.length === 1) {
      return {
        xmlContent,
        wasMerged: false,
        wasModified: false,
        message: 'Only one part found - merge requires two parts (notation + TAB)'
      };
    }

    if (parts.length > 2) {
      return {
        xmlContent,
        wasMerged: false,
        wasModified: false,
        message: `Found ${parts.length} parts - merge only supports exactly 2 parts`
      };
    }

    // parts.length === 2 but not one notation + one TAB
    const notationCount = parts.filter(p => p.isNotation).length;
    const tabCount = parts.filter(p => p.isTab).length;

    return {
      xmlContent,
      wasMerged: false,
      wasModified: false,
      message: `Cannot merge: found ${notationCount} notation part(s) and ${tabCount} TAB part(s). Need exactly 1 of each.`
    };
  }

  // Perform the merge
  const notationPart = parts.find(p => p.isNotation)!;
  const tabPart = parts.find(p => p.isTab)!;

  try {
    performMerge(doc, notationPart, tabPart);
  } catch (mergeError) {
    console.error('Error during MusicXML merge:', mergeError);
    return {
      xmlContent,
      wasMerged: false,
      wasModified: false,
      message: 'Failed to merge parts - returning original file'
    };
  }

  // Serialize the document back to string
  const serializer = new XMLSerializer();
  let mergedXml = serializer.serializeToString(doc);

  // Add XML declaration if it was present in the original
  if (xmlContent.startsWith('<?xml')) {
    const xmlDeclMatch = xmlContent.match(/<\?xml[^?]*\?>/);
    if (xmlDeclMatch && !mergedXml.startsWith('<?xml')) {
      mergedXml = xmlDeclMatch[0] + '\n' + mergedXml;
    }
  }

  return {
    xmlContent: mergedXml,
    wasMerged: true,
    wasModified: true,
    message: 'Merged notation and TAB tracks'
  };
}
